// Role-first navigation config. Each role gets its own purpose-built IA
// instead of the same 27-item skeleton with items filtered out.
// Consumed by Sidebar.js when the nav_v2 feature flag is on.
//
// Item shape: { path, label, icon, exact?, permission?, badge? }
// Section shape: { label, items[] }

const commonAmbient = { path: '/practice-view/ambient-scribe', label: 'Ambient Scribe', icon: 'mic', permission: 'scribe:view' };
const commonPatientFlow = { path: '/practice-view/patient-flow', label: 'Patient Flow', icon: 'activity', permission: 'flow:view' };
const commonVoiceAI = { path: '/practice-view/voice-ai', label: 'Voice AI', icon: 'phone', permission: 'voice:call' };
const commonPatientDir = { path: '/practice-view/patients', label: 'Patient Directory', icon: 'users', permission: 'patients:view' };
const commonCareCal = { path: '/practice-view/care-hub', label: 'Care Calendar', icon: 'calendar', permission: 'appointments:view' };
const commonWaitlist = { path: '/practice-view/waitlist', label: 'Waitlist', icon: 'clock', permission: 'waitlist:view' };
const commonIntakeICR = { path: '/practice-view/insurance-change-requests', label: 'Intake & Insurance Changes', icon: 'shield', permission: 'intake:manage' };
const commonPreVisit = { path: '/practice-view/operations-readiness', label: 'Pre-Visit Readiness', icon: 'alert', permission: 'reports:dashboard' };
const commonAIPredictions = { path: '/practice-view/ai-insights', label: 'AI Predictions', icon: 'zap', permission: 'ai:insights' };
const commonReputation = { path: '/practice-view/reputation', label: 'Reputation', icon: 'star', permission: 'reputation:view' };
const commonCampaigns = { path: '/practice-view/campaigns', label: 'Campaigns', icon: 'megaphone', permission: 'campaigns:view' };
const commonSettings = { path: '/practice-view/practice-settings', label: 'Practice Settings', icon: 'settings', permission: 'settings:view' };
const commonActivity = { path: '/practice-view/activity', label: 'Activity Log', icon: 'clock', permission: 'reports:activity' };

// Phase 6 merges Messaging + Notifications into a single Communication hub.
const communicationHub = { path: '/practice-view/communication', label: 'Communication', icon: 'chat', permission: 'sms:view' };

export const NAV_CONFIG = {
  care_provider: [
    {
      label: 'My Day',
      items: [
        { path: '/practice-view/care-hub', label: "Today's Appointments", icon: 'calendar', permission: 'appointments:view' },
        commonPatientDir,
        communicationHub,
      ],
    },
    {
      label: 'Documentation',
      items: [
        commonAmbient,
        commonPatientFlow,
      ],
    },
    {
      label: 'Support',
      items: [
        commonVoiceAI,
      ],
    },
  ],

  clinical_admin: [
    {
      label: 'My Day',
      items: [
        { path: '/practice-view', label: 'Dashboard', icon: 'home', exact: true, permission: 'reports:dashboard' },
        commonCareCal,
        commonPatientDir,
        communicationHub,
      ],
    },
    {
      label: 'Pre-Visit',
      items: [
        commonPreVisit,
        commonIntakeICR,
        commonWaitlist,
      ],
    },
    {
      label: 'Clinical',
      items: [
        commonAmbient,
        commonPatientFlow,
        commonAIPredictions,
        { path: '/practice-view/ai-queue', label: 'AI Review Hub', icon: 'sparkle', permission: 'sms:ai_queue' },
      ],
    },
    {
      label: 'Growth',
      items: [
        commonCampaigns,
        commonReputation,
        commonVoiceAI,
      ],
    },
    {
      label: 'Reports',
      items: [
        { path: '/practice-view/reports', label: 'Insights', icon: 'chart', permission: 'reports:dashboard' },
      ],
    },
    {
      label: 'Practice',
      items: [
        commonSettings,
        commonActivity,
        { path: '/practice-view/compliance', label: 'Compliance', icon: 'shield', permission: 'compliance:view' },
      ],
    },
  ],

  practice_admin: [
    {
      label: 'My Day',
      items: [
        { path: '/practice-view', label: 'Dashboard', icon: 'home', exact: true, permission: 'reports:dashboard' },
        { path: '/practice-view/roi-dashboard', label: 'ROI Dashboard', icon: 'trending', permission: 'reports:dashboard' },
        commonCareCal,
        communicationHub,
      ],
    },
    {
      label: 'Pre-Visit',
      items: [
        commonPreVisit,
        commonIntakeICR,
        commonWaitlist,
      ],
    },
    {
      label: 'Clinical',
      items: [
        commonAmbient,
        commonPatientFlow,
        commonAIPredictions,
        { path: '/practice-view/ai-queue', label: 'AI Review Hub', icon: 'sparkle', permission: 'sms:ai_queue' },
      ],
    },
    {
      label: 'Growth',
      items: [
        commonCampaigns,
        commonReputation,
        commonVoiceAI,
      ],
    },
    {
      label: 'Revenue',
      items: [
        { path: '/practice-view/revenue-flow', label: 'RevenueFlow', icon: 'trending', permission: 'billing:revenue_flow' },
        { path: '/practice-view/billing-escalations', label: 'Escalations', icon: 'alert', permission: 'billing:escalations' },
        { path: '/practice-view/payment-settings', label: 'Payment Settings', icon: 'creditcard', permission: 'billing:configure' },
      ],
    },
    {
      label: 'Reports',
      items: [
        { path: '/practice-view/reports', label: 'Insights', icon: 'chart', permission: 'reports:dashboard' },
      ],
    },
    {
      label: 'Practice',
      items: [
        commonSettings,
        { path: '/practice-view/tag-management', label: 'Tags & Labels', icon: 'tag', permission: 'tags:manage' },
        { path: '/practice-view/compliance', label: 'Compliance', icon: 'shield', permission: 'compliance:view' },
        commonActivity,
      ],
    },
  ],

  // super_admin gets practice_admin view + Admin drawer (Phase 7)
  super_admin: null, // resolved to practice_admin at runtime
};

// Testing tools grouped for the admin drawer / dev-env only surfacing.
export const TESTING_ITEMS = [
  { path: '/practice-view/testbed', label: 'TestBed', icon: 'beaker', permission: 'campaigns:run' },
  { path: '/practice-view/ehr-live-test', label: 'EHR Live Test', icon: 'zap', permission: 'ehr:sync' },
  { path: '/practice-view/payment-test', label: 'Payment Test', icon: 'creditcard', permission: 'billing:configure' },
];

export const ADMIN_NAV_CONFIG = [
  {
    label: 'Overview',
    items: [
      { path: '/admin', label: 'Dashboard', icon: 'home', exact: true },
    ],
  },
  {
    label: 'Tenancy',
    items: [
      { path: '/admin/practice-groups', label: 'Practice Hub', icon: 'folder' },
      { path: '/admin/users', label: 'Users', icon: 'users' },
      { path: '/admin/identity', label: 'Identity & SSO', icon: 'shield' },
      { path: '/admin/migration', label: 'V1 Migration', icon: 'folder' },
      { path: '/admin/phone-management', label: 'Phone Management', icon: 'phone' },
    ],
  },
  {
    label: 'Audit',
    items: [
      { path: '/admin/audit-log', label: 'Audit Log', icon: 'clock' },
      { path: '/admin/hipaa-audit-log', label: 'HIPAA Audit', icon: 'file' },
      { path: '/admin/logs', label: 'Platform Logs', icon: 'file' },
      { path: '/admin/baa', label: 'BAA Tracking', icon: 'shield' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { path: '/admin/compliance', label: 'Compliance Engine', icon: 'shield' },
    ],
  },
];

export function resolveNavForRole(role) {
  if (!role) return [];
  if (role === 'super_admin') return NAV_CONFIG.practice_admin;
  return NAV_CONFIG[role] || [];
}
